/*
 *******************************************************************************
 *
 *  Filename:   /redux/slices/notifications.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   In-app notification state. Holds the latest notification list,
 *              the unread badge count, and a loading flag. This slice is an
 *              API cache (driven by the Dashboard-shell polling loop), so it is
 *              NOT persisted across refreshes — see the persist whitelist in
 *              store.js.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Tuesday, May 27, 2026
 *
 *  Notes:
 *      - setNotifications / setUnreadCount take a raw payload (array / number)
 *        rather than the { field: value } shape used by updateFieldReducer,
 *        mirroring the dashboard slice's object/array setters.
 *      - markItemsRead / dismissItems take a raw array of ids so optimistic UI
 *        can flip rows before the API round-trips.
 *
 *  Revisions:
 *              2026/05/27  NJN Initial Build
 *
 *  Copyright (c) 2026 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

const toIdSet = (ids) => new Set((ids || []).map((id) => Number(id)));

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Raw payload: an array of notification rows.
    setNotifications: (state, action) => {
      state.items = Array.isArray(action.payload) ? action.payload : [];
      state.error = null;
    },
    // Raw payload: a number.
    setUnreadCount: (state, action) => {
      const value = Number(action.payload);
      state.unreadCount = Number.isNaN(value) ? 0 : value;
    },
    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },
    // Raw payload: an array of NOTIFICATIONID values.
    markItemsRead: (state, action) => {
      const ids = toIdSet(action.payload);
      let cleared = 0;
      state.items = state.items.map((item) => {
        if (ids.has(Number(item.NOTIFICATIONID)) && !item.IS_READ) {
          cleared += 1;
          return { ...item, IS_READ: 1, READ_AT: new Date().toISOString() };
        }
        return item;
      });
      state.unreadCount = Math.max(0, state.unreadCount - cleared);
    },
    markAllItemsRead: (state) => {
      const now = new Date().toISOString();
      state.items = state.items.map((item) =>
        item.IS_READ ? item : { ...item, IS_READ: 1, READ_AT: now },
      );
      state.unreadCount = 0;
    },
    // Raw payload: an array of NOTIFICATIONID values.
    dismissItems: (state, action) => {
      const ids = toIdSet(action.payload);
      let clearedUnread = 0;
      state.items = state.items.filter((item) => {
        if (ids.has(Number(item.NOTIFICATIONID))) {
          if (!item.IS_READ) clearedUnread += 1;
          return false;
        }
        return true;
      });
      state.unreadCount = Math.max(0, state.unreadCount - clearedUnread);
    },
  },
});

export const {
  setNotifications,
  setUnreadCount,
  setLoading,
  markItemsRead,
  markAllItemsRead,
  dismissItems,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
